import { useNavigate, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useState, useEffect, useRef, useReducer, lazy, Suspense } from 'react';
import { auth, db } from '../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useThemeColors } from '../context/ThemeContext';
import { useIsDesktop } from '../hooks/useIsDesktop';
import { dsv2, dsv2Light } from '../styles/designSystemV2';
import { useTranslation } from 'react-i18next';
import { Menu, LayoutGrid, Check, Repeat, ChevronDown, Sparkles } from 'lucide-react';
import NavCustomizer from './NavCustomizer';
import { getNavLayout, configForRole } from '../utils/navItems';
import { snapshotMigrationConfig } from '../utils/migrationConfig';
import { setNavConfig as apiSetNavConfig } from '../api/users';
import { fetchVehicles, fetchFleetRecords } from '../api/vehicles';
import { getVehicleAlerts, getFleetHealth } from '../utils/maintenanceRules';
import { trasPrimerPintado } from '../utils/trasPrimerPintado';
import MiniHealthGauge from './MiniHealthGauge';
import { useAssistantStore, openAssistant } from '../assistant/assistantStore';

// Perezoso: arrastra @dnd-kit (~112 KB sin comprimir) y solo se usa al editar el menú.
const NavEditGrid = lazy(() => import('./NavEditGrid'));

// Capas del menú EXPANDIDO. Los banners fijos de la franja de abajo viven por
// encima del nav (PushPrompt en 8000, VerifyEmailBanner en zLayer.verifyBanner
// = 8900) y ocupan exactamente el sitio donde crece el menú: por eso el botón de
// cerrar sesión no respondía al toque — no estaba roto, estaba debajo. Mientras
// el menú está abierto, el menú y su velo pasan por encima de los dos; el velo
// también, para que ningún banner intercepte un toque donde no se ve (cae en el
// velo y cierra el menú, que es lo que espera cualquiera al tocar fuera).
// Se quedan por DEBAJO de zLayer.suspensionBanner (9200) y blockingModal (9300):
// esas capas existen para desbloquear al usuario y taparlas sería el mismo bug.
// COLAPSADO el nav no sube: ahí tiene que seguir cediendo ante el asistente.
const Z_MENU_ABIERTO = 9100;
const Z_MENU_VELO = 9050;
const Z_NAV_COLAPSADO = 1600;

// Singleton: aunque varias páginas monten <BottomNav /> (además del global en
// App.js), solo UNA instancia se renderiza (evita el nav fantasma detrás).
let bnPrimaryId = null;
const bnSubscribers = new Set();

export default function BottomNav() {
  const { t } = useTranslation();
  const { userProfile, logout, effectiveRole, availableRoles, setActiveRole, refreshProfile } = useAuth();
  const { getItemCount } = useCart();
  const { isDark } = useThemeColors();
  const tokens = isDark ? dsv2 : dsv2Light;
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const location = useLocation();
  const { voiceStatus } = useAssistantStore();
  const [unreadCount,    setUnreadCount]    = useState(0);
  const [msgUnreadCount, setMsgUnreadCount] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [dragPx, setDragPx] = useState(null);   // px revelados mientras se arrastra
  const [fullH, setFullH] = useState(0);          // alto natural del panel de opciones
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [fleetScore, setFleetScore] = useState(null); // salud de la flota (cliente)
  const longPressRef = useRef(null);
  const longPressedRef = useRef(false); // el long-press disparó: tragarse el click siguiente
  const menuRef = useRef(null);
  const menuTabRef = useRef(null);
  const extrasInnerRef = useRef(null);
  const dragStartYRef = useRef(null);
  const dragStartRevealRef = useRef(0);
  const lastRevealRef = useRef(0);
  const draggedRef = useRef(false);

  // Salud de la flota para el ícono "Salud" del nav (solo cliente).
  //
  // 🔴 ESTE NÚMERO NO PUEDE COMPETIR CON EL CONTENIDO. Medido contra producción
  // (móvil, 4G lenta): esta cadena repetía la del home —`GET /vehicles` y un
  // `GET /vehicles/:id/records` por carro— y salían las OCHO peticiones a la vez
  // que las que el cliente está esperando ver, para pintar un numerito de 24 px.
  //
  // Dos cosas lo cierran, y hacen falta las dos:
  //   1. AQUÍ: se pide CUANDO YA HAY ALGO EN PANTALLA (`trasPrimerPintado`). La
  //      barra ya se ve; el ícono de salud aparece después. ⚠️ Antes esto usaba
  //      `requestIdleCallback` y se midió contra producción que NO basta: «el
  //      hilo está libre» ocurre ANTES del primer pintado, así que la carga
  //      seguía cayendo justo encima de lo que el cliente espera ver.
  //   2. En `src/api/vehicles.js`: la respuesta de la flota se comparte durante
  //      unos segundos, así que al llegar el momento esto reaprovecha lo que ya
  //      pidió el home en vez de volver a pedirlo. Sin (2), diferir solo movería
  //      las peticiones de sitio; sin (1), lo compartido no llegaría a tiempo.
  //
  // ⚠️ El menú está montado en TODAS las pantallas, no solo en el home: quien
  // entra directo a otra ruta no tiene quien le pida la flota, así que esta
  // carga tiene que seguir ocurriendo siempre — diferida, nunca omitida.
  useEffect(() => {
    if (effectiveRole !== 'client' || !auth.currentUser) { setFleetScore(null); return; }
    let cancelled = false;
    const cargarSalud = async () => {
      // El menú pudo desmontarse (o cambiar de rol) mientras se esperaba el
      // pintado: entonces no se pide nada. `trasPrimerPintado` no se cancela —
      // su tope tiene que llegar siempre—, así que la puerta se pone aquí.
      if (cancelled) return;
      try {
        // El número que pinta el ícono de "Salud" del menú. Con el dominio
        // encendido y este bloque leyendo Firestore saldría un 100 en verde
        // para todo el mundo: no un fallo, una tranquilidad falsa.
        const fuente = await snapshotMigrationConfig()
          .then(s => s.readsFrom('vehicles'))
          .catch(() => 'firestore');
        const vehicles = fuente === 'postgres'
          ? (await fetchVehicles({ page: 1 })).vehicles
          : (await getDocs(query(collection(db, 'vehicles'), where('userId', '==', auth.currentUser.uid)))).docs
              .map(d => ({ id: d.id, ...d.data() }));
        if (!vehicles.length) { if (!cancelled) setFleetScore(null); return; }
        let recs = [];
        if (fuente === 'postgres') {
          recs = await fetchFleetRecords(vehicles);
        } else {
          const ids = vehicles.map(v => v.id);
          for (let i = 0; i < ids.length; i += 10) {
            const rs = await getDocs(query(collection(db, 'maintenanceRecords'), where('vehicleId', 'in', ids.slice(i, i + 10))));
            rs.forEach(d => recs.push({ id: d.id, ...d.data() }));
          }
        }
        const alertArrays = vehicles.map(v => getVehicleAlerts(v, recs.filter(r => r.vehicleId === v.id)));
        if (!cancelled) setFleetScore(getFleetHealth(alertArrays));
      } catch (_) { if (!cancelled) setFleetScore(null); }
    };
    // El tope de `trasPrimerPintado` no es adorno: una pestaña que nunca pinta
    // dejaría el ícono de salud vacío para siempre.
    trasPrimerPintado(cargarSalud);
    return () => { cancelled = true; };
  }, [effectiveRole, userProfile?.id]);

  const measureFull = () => (extrasInnerRef.current ? extrasInnerRef.current.offsetHeight : 0);
  const openMenu = () => { setFullH(measureFull()); setShowMenu(true); };
  const closeMenu = () => { setShowMenu(false); setDragPx(null); setConfirmLogout(false); setEditMode(false); };
  const toggleMenu = () => { if (showMenu) closeMenu(); else openMenu(); };

  // Modo editar (reordenar íconos arrastrando).
  const enterEdit = () => { setShowMenu(true); setEditMode(true); };
  const exitEdit = () => { setEditMode(false); };
  // Orden por rol. El API lo guarda por (usuario, ROL) igual que el dot-path
  // `navConfig.<role>` de Firestore: guardar el menú de taller NO puede pisar el
  // de cliente. Por eso el rol viaja en los dos caminos.
  //
  // (Este comentario decía antes que el menú SE QUEDABA en Firestore porque el
  // API no tenía ruta ni esquema. Ya los tiene: `PUT /users/me/nav-config`, con
  // el rol en el cuerpo. Lo que sigue siendo cierto es que NO se puede colar en
  // `PATCH /users/me`: esa lista de permitidos responde 400 nombrando el campo
  // que sobra, así que tumbaría el guardado entero en vez de ignorarlo.)
  //
  // ⚠️ Esta escritura es de fondo: nadie mira una pantalla que diga "guardado".
  // No puede tumbar la barra, pero el motivo tiene que llegar a la consola —
  // tragárselo en silencio es lo que deja un menú que "no se guarda" sin rastro.
  const saveNavConfig = (orderedIds) => {
    if (!auth.currentUser) return;
    const guardar = async () => {
      const snap = await snapshotMigrationConfig();
      if (snap.readsFrom('users') === 'postgres') await apiSetNavConfig(effectiveRole, orderedIds);
      else await updateDoc(doc(db, 'users', auth.currentUser.uid), { [`navConfig.${effectiveRole}`]: orderedIds });
    };
    guardar()
      .then(() => refreshProfile && refreshProfile())
      .catch((e) => console.warn('[nav] no se pudo guardar el orden del menú:', e?.message || e));
  };

  // Arrastre tipo "cortina de notificaciones": el panel sigue el dedo.
  const onDragStart = (e) => {
    dragStartYRef.current = e.touches[0].clientY;
    dragStartRevealRef.current = showMenu ? measureFull() : 0;
    draggedRef.current = false;
  };
  const onDragMove = (e) => {
    if (dragStartYRef.current == null) return;
    const h = measureFull();
    const dy = dragStartYRef.current - e.touches[0].clientY; // arrastrar arriba = positivo
    if (Math.abs(dy) > 6) { draggedRef.current = true; clearTimeout(longPressRef.current); }
    const px = Math.max(0, Math.min(dragStartRevealRef.current + dy, h));
    lastRevealRef.current = px;
    if (h && h !== fullH) setFullH(h);
    setDragPx(px);
  };
  const onDragEnd = () => {
    if (dragStartYRef.current == null) return;
    dragStartYRef.current = null;
    if (draggedRef.current) {
      const h = measureFull() || fullH;
      setFullH(h);
      setShowMenu(lastRevealRef.current > h * 0.35); // snap abrir/cerrar
    }
    setDragPx(null);
  };

  // Mantener pulsado un ícono entra a modo editar (estilo iPhone).
  const startLongPress = () => {
    clearTimeout(longPressRef.current);
    longPressRef.current = setTimeout(() => {
      if (!draggedRef.current) { longPressedRef.current = true; enterEdit(); }
    }, 600);
  };
  const cancelLongPress = () => { clearTimeout(longPressRef.current); };

  // Remedir el alto del panel al cambiar de contenido (menú ↔ editar).
  useEffect(() => {
    if (!showMenu) return;
    const id = requestAnimationFrame(() => {
      if (extrasInnerRef.current) setFullH(extrasInnerRef.current.offsetHeight);
    });
    return () => cancelAnimationFrame(id);
  }, [showMenu, editMode]);

  const cartCount = getItemCount();

  // Guard de singleton: solo la primera instancia montada se renderiza.
  const navIdRef = useRef(null);
  if (navIdRef.current === null) navIdRef.current = Symbol('bottomnav');
  const [, forceBnUpdate] = useReducer((c) => c + 1, 0);
  useEffect(() => {
    const tryClaim = () => { if (bnPrimaryId === null) bnPrimaryId = navIdRef.current; forceBnUpdate(); };
    bnSubscribers.add(tryClaim);
    tryClaim();
    return () => {
      bnSubscribers.delete(tryClaim);
      if (bnPrimaryId === navIdRef.current) { bnPrimaryId = null; bnSubscribers.forEach((f) => f()); }
    };
  }, []);

  useEffect(() => {
    if (!auth.currentUser) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', auth.currentUser.uid),
      where('read', '==', false)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setUnreadCount(snapshot.size);
    }, (err) => { if (err?.code !== 'permission-denied') console.error('BottomNav snapshot:', err); });

    // Unread direct messages
    const qMsg = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', auth.currentUser.uid)
    );
    const unsubMsg = onSnapshot(qMsg, snapshot => {
      const count = snapshot.docs.filter(d => {
        const data = d.data();
        return (data.unreadCounts?.[auth.currentUser.uid] || 0) > 0;
      }).length;
      setMsgUnreadCount(count);
    }, (err) => { if (err?.code !== 'permission-denied') console.error('BottomNav msg snapshot:', err); });

    return () => { unsubscribe(); unsubMsg(); };
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) closeMenu();
    };
    if (showMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  const hiddenPaths = ['/', '/login', '/workshop/setup'];
  const shouldHide = hiddenPaths.some(p =>
    p === '/' ? location.pathname === '/' : location.pathname.startsWith(p)
  );

  if (shouldHide) return null;
  if (isDesktop) return null;
  if (bnPrimaryId !== navIdRef.current) return null; // singleton: solo una instancia

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (e) {}
  };

  const menuNavigate = (path) => {
    setShowMenu(false);
    navigate(path);
  };

  const role = effectiveRole;

  const labelForRole = (r) => r === 'workshop_owner' ? t('nav.workshop')
    : r === 'mechanic' ? t('profile.role.mechanic')
    : r === 'admin' ? t('sidebar.sectionAdmin')
    : t('profile.role.client');
  const homeForRole = (r) => r === 'workshop_owner' ? '/workshop/dashboard'
    : r === 'mechanic' ? '/mechanic/dashboard'
    : r === 'admin' ? '/admin/dashboard'
    : '/home';
  const canSwitchRole = availableRoles.length > 1;
  const switchTo = (r) => { setActiveRole(r); setShowMenu(false); navigate(homeForRole(r)); };

  const menuTab = { id: 'menu', label: t('nav.menu'), Icon: Menu, path: null, matchPaths: [] };

  // Modelo unificado para TODOS los roles (incluido admin): barra (4) + panel,
  // reordenables. El orden vive en users.navConfig.
  const navConfig = userProfile?.navConfig;
  const layout = getNavLayout(role, navConfig, t);
  // Cuando hay una llamada de voz activa, el asistente aparece en la barra (rojo,
  // junto a Menú) aunque el usuario lo tenga en el panel. Si ya lo tiene en la
  // barra, se queda en su lugar.
  const voiceActive = !!voiceStatus && voiceStatus !== 'idle';
  const aInBar = layout.bar.some(x => x.id === 'assistant');
  const aTab = { id: 'assistant', label: t('assistant.navTitle'), Icon: Sparkles, path: null, matchPaths: [], isAssistant: true };
  const barItems = (voiceActive && !aInBar)
    ? (layout.bar.length ? [...layout.bar.slice(0, -1), aTab] : [aTab])
    : layout.bar;
  const tabs = [...barItems, menuTab];
  const menuItems = layout.panel;
  const editItems = layout.all; // ítems arrastrables (sin Menú)

  // Quién puede personalizar arrastrando: proveedores, clientes-pago (tier futuro)
  // y la cuenta admin EN CUALQUIER contexto (premium total, incluso al actuar como cliente).
  const isPayingClient = userProfile?.plan === 'pro' || userProfile?.clientPlan === 'pro';
  const isAdminAccount = availableRoles.includes('admin');
  const canCustomize = role === 'workshop_owner' || role === 'mechanic' || isAdminAccount || isPayingClient;

  const isActive = (tab) => tab.matchPaths.some(mp => mp.endsWith('/') ? location.pathname.startsWith(mp) : location.pathname === mp);

  // Badge por id (las listas unificadas no llevan badge embebido).
  const badgeForId = (id) =>
    id === 'inbox' ? unreadCount
    : id === 'messages' ? msgUnreadCount
    : (id === 'cart' || id === 'marketplace' || id === 'store') ? cartCount
    : 0;

  // Cuánto del panel está revelado (px): durante el arrastre sigue el dedo; si no,
  // está todo (abierto) o nada (cerrado). isOpenish = hay algo revelado.
  const revealPx = dragPx != null ? dragPx : (showMenu ? fullH : 0);
  const isOpenish = showMenu || revealPx > 4 || editMode;

  return createPortal(
    <>
      {/* Overlay para cerrar tocando fuera (se atenúa según el arrastre) */}
      {isOpenish && (
        <div
          onClick={closeMenu}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', opacity: showMenu ? 1 : (fullH ? Math.min(1, revealPx / fullH) : 0), transition: dragPx != null ? 'none' : 'opacity .2s ease', zIndex: Z_MENU_VELO }}
        />
      )}

      {/* UN SOLO elemento: la misma barra del bottom nav que se EXPANDE.
          Arriba los tabs (siempre); al expandir, debajo aparece el resto.
          Arrastra hacia arriba para abrir, hacia abajo / tocar fuera para cerrar. */}
      <nav
        ref={(el) => { menuRef.current = el; }}
        onTouchStart={editMode ? undefined : onDragStart}
        onTouchMove={editMode ? undefined : onDragMove}
        onTouchEnd={editMode ? undefined : onDragEnd}
        style={{
          position: 'fixed',
          left: '50%', bottom: 'max(18px, env(safe-area-inset-bottom))',
          transform: 'translateX(-50%)',
          zIndex: isOpenish ? Z_MENU_ABIERTO : Z_NAV_COLAPSADO,
          width: 'min(92vw, 360px)',
          overflow: 'hidden',
          background: tokens.glass.bg,
          backdropFilter: tokens.glass.blur,
          WebkitBackdropFilter: tokens.glass.blur,
          border: `1px solid ${tokens.outline}`,
          // Colapsado = pill perfecto; expandido = esquinas bien redondeadas (28).
          // Sin transición de tiempo: el redondeo acompaña al crecimiento de altura.
          borderRadius: isOpenish ? 28 : tokens.radius.pill,
          boxShadow: tokens.shadow.lg,
          paddingTop: isOpenish ? 6 : 0,
          touchAction: 'none',
        }}
      >
        {/* Fila de tabs (los íconos normales) — al jalar, suben hasta ARRIBA.
            En modo editar se colapsa: la primera fila de la grilla la representa. */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 4,
          padding: editMode ? '0 8px' : '10px 8px',
          maxHeight: editMode ? 0 : 90,
          opacity: editMode ? 0 : 1,
          overflow: 'hidden',
          transition: 'max-height .26s cubic-bezier(.22,1,.36,1), opacity .18s ease, padding .26s ease',
        }}>
          {tabs.map(tab => {
            const active = tab.id !== 'menu' && isActive(tab);
            const menuActive = tab.id === 'menu' && showMenu;
            const assistantLive = tab.id === 'assistant' && voiceActive; // llamada en curso
            const isCurrentActive = active || menuActive || assistantLive;
            const TabIcon = tab.Icon;
            const badge = tab.id === 'menu' ? (unreadCount + msgUnreadCount) : badgeForId(tab.id);
            return (
              <button
                key={tab.id}
                ref={tab.id === 'menu' ? menuTabRef : null}
                onTouchStart={canCustomize && tab.id !== 'menu' && tab.id !== 'assistant' ? startLongPress : undefined}
                onTouchEnd={canCustomize ? cancelLongPress : undefined}
                onClick={() => {
                  if (longPressedRef.current) { longPressedRef.current = false; return; } // fue long-press
                  if (draggedRef.current) { draggedRef.current = false; return; } // fue arrastre, no tap
                  if (editMode) { exitEdit(); return; }
                  if (tab.id === 'menu') toggleMenu();
                  else if (tab.id === 'assistant') { if (showMenu) closeMenu(); openAssistant(); }
                  else { if (showMenu) closeMenu(); navigate(tab.path); }
                }}
                onMouseDown={(e) => { if (tab.id === 'menu') e.stopPropagation(); }}
                style={{
                  background: assistantLive ? tokens.red : (isCurrentActive
                    ? 'linear-gradient(0deg, rgba(230,33,23,0.32) 0%, rgba(230,33,23,0.10) 50%, transparent 100%)'
                    : 'transparent'),
                  border: 'none', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  padding: '6px 12px', minWidth: 58, borderRadius: tokens.radius.pill,
                  color: assistantLive ? '#fff' : (isCurrentActive ? tokens.red : tokens.textMuted),
                  fontFamily: tokens.font.body,
                  transition: 'background .2s ease, color .18s ease',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <div style={{ position: 'relative', display: 'flex' }}>
                  {tab.id === 'stats' && fleetScore != null
                    ? <MiniHealthGauge score={fleetScore} size={24} />
                    : <TabIcon size={22} strokeWidth={isCurrentActive ? 2.4 : 2} />}
                  {badge > 0 && (
                    <span style={{ position: 'absolute', top: -5, right: -7, minWidth: 16, height: 16, padding: '0 4px', background: tokens.red, color: '#fff', borderRadius: 999, border: `2px solid ${tokens.surfaceLow}`, fontSize: 9, fontWeight: 700, fontFamily: tokens.font.mono, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>
                      {badge > 9 ? '9+' : badge}
                    </span>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: isCurrentActive ? 700 : 500, letterSpacing: '0.02em', lineHeight: 1 }}>
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>

        {/* Resto de opciones — aparece DEBAJO de los tabs, revelándose con el arrastre.
            Siempre montado (para medir su alto y revelarlo progresivamente). */}
        <div style={{
          height: revealPx,
          overflow: 'hidden',
          transition: dragPx != null ? 'none' : 'height .3s cubic-bezier(.22,1,.36,1)',
        }}>
          {/* El contenido queda fijo (sin fade): el recorte de altura lo descubre
              subiendo recto desde la barra, como una sola pieza arrastrada. */}
          <div ref={extrasInnerRef}>
            {editMode ? (
            <Suspense fallback={null}><NavEditGrid items={editItems} onChange={saveNavConfig} onDone={exitEdit} tokens={tokens} t={t} /></Suspense>
            ) : (
            <>
            {/* separador justo debajo de los tabs */}
            <div style={{ height: 1, background: tokens.outline, margin: '2px 14px 8px' }} />

            {canCustomize && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 14px 6px' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); enterEdit(); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: tokens.radius.pill, cursor: 'pointer', background: `${tokens.red}0d`, border: `1px solid ${tokens.red}44`, color: tokens.red }}
                >
                  <LayoutGrid size={11} /> {t('nav.editButton')}
                </button>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6, padding: '0 12px' }}>
              {menuItems.map((it, i) => {
                const TileIcon = it.Icon;
                const itBadge = (it.id ? badgeForId(it.id) : 0) || it.badge || 0;
                return (
                  <button
                    key={'m' + i}
                    onTouchStart={canCustomize ? startLongPress : undefined}
                    onTouchEnd={canCustomize ? cancelLongPress : undefined}
                    onClick={() => {
                      if (longPressedRef.current) { longPressedRef.current = false; return; }
                      if (it.id === 'assistant') { setShowMenu(false); openAssistant(); return; }
                      menuNavigate(it.path);
                    }}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '8px 4px', borderRadius: tokens.radius.pill, background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: tokens.font.body, color: tokens.textMuted, WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div style={{ position: 'relative', display: 'flex' }}>
                      {it.id === 'stats' && fleetScore != null
                        ? <MiniHealthGauge score={fleetScore} size={26} />
                        : <TileIcon size={22} color={tokens.textMuted} />}
                      {itBadge > 0 && (
                        <span style={{ position: 'absolute', top: -5, right: -7, minWidth: 16, height: 16, padding: '0 4px', background: tokens.red, color: '#fff', borderRadius: 999, fontSize: 9, fontWeight: 700, fontFamily: tokens.font.mono, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `2px solid ${tokens.surfaceLow}` }}>
                          {itBadge > 9 ? '9+' : itBadge}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 500, color: tokens.textMuted, textAlign: 'center', lineHeight: 1.1, letterSpacing: '0.02em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {it.label}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Cerrar sesión — arriba del cambio de roles, con confirmar de 2 toques */}
            <div style={{ padding: '10px 14px 4px' }}>
              <button
                onClick={() => { if (confirmLogout) handleLogout(); else setConfirmLogout(true); }}
                style={{ width: '100%', padding: '13px', backgroundColor: confirmLogout ? tokens.red : tokens.redSoft, border: 'none', borderRadius: tokens.radius.lg, color: confirmLogout ? '#fff' : tokens.red, fontSize: 14, fontWeight: confirmLogout ? 700 : 600, cursor: 'pointer', fontFamily: tokens.font.body }}
              >
                {confirmLogout ? t('nav.logoutConfirm') : t('profile.logout')}
              </button>
            </div>

            {canSwitchRole && (
              <div style={{ padding: '10px 14px 2px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: tokens.textDim, fontFamily: tokens.font.mono, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Repeat size={11} /> {t('sidebar.switchContext')}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {availableRoles.map(r => (
                    <button
                      key={r}
                      onClick={() => switchTo(r)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 12px', borderRadius: tokens.radius.pill, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: r === role ? `1px solid ${tokens.red}` : `1px solid ${tokens.outline}`, background: r === role ? `${tokens.red}0d` : tokens.surfaceHigh, color: r === role ? tokens.red : tokens.textMuted, fontFamily: tokens.font.body }}
                    >
                      {r === role && <Check size={12} />} {labelForRole(r)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Cerrar menú — abajo de Cerrar sesión para que ese no sea el último (evita taps accidentales) */}
            <div style={{ padding: '6px 14px 2px' }}>
              <button
                onClick={closeMenu}
                style={{ width: '100%', padding: '11px', backgroundColor: 'transparent', border: 'none', borderRadius: tokens.radius.lg, color: tokens.textMuted, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: tokens.font.body, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <ChevronDown size={15} /> {t('nav.closeMenu')}
              </button>
            </div>
            </>
            )}
          </div>
        </div>
      </nav>

      {/* `navConfig` es un MAPA por rol, no una lista: pasarlo entero dejaba a la
          hoja con un objeto donde espera un array (`selected.includes`). Ahora
          que guarda por rol, tiene que LEER por rol o enseñaría vacío lo que sí
          está guardado. */}
      {showCustomizer && (
        <NavCustomizer
          role={role}
          currentConfig={configForRole(role, userProfile?.navConfig) || []}
          onClose={() => setShowCustomizer(false)}
          onSaved={() => {}}
        />
      )}

    </>,
    document.body
  );
}
